/**
 * 서버 구성 편집기 — 순수 렌더 함수들.
 *
 * 화면 계약:
 *   H1                : "{거래처} {섀시 모델} {신품|중고} 서버 구성" (2026-08-14)
 *   헤더 카드         : 서버 모델 → 서버 컨디션 → 서버 판매 가격 → 완료
 *                       (서버 대수는 딜 소유라 여기 없다 — `CONFIG2-018`)
 *   구성 grid 7열     : 카테고리 | 품목 | 디스크립션 | 수량 | 소스 | 판매단가 | 작업
 *   trailing blank row: 저장하지 않는 빈 행 정확히 하나 (품목 검색만 노출)
 *
 * 번들가와 구성품 판매가 합계는 독립이다. 합계는 표시 전용이며 번들가를
 * 자동으로 덮어쓰지 않는다(자동 승격 금지).
 */

import { escapeHtml, catalogHitDescription } from '../../lib/cells';
import { selectedSourceValue, sourceSelectHtml } from '../../lib/source-options';
// 딥 경로 import — 배럴은 브라우저 번들에서 hono/jwt 해석 실패.
import { SPEC_CATEGORIES } from '@hcltech/shared/server-config/spec-category';
import { serverConditionLabel } from '@hcltech/shared/condition/server-condition';
import { buildServerConfigName, firstDescriptionLine } from '@hcltech/shared/server-config/config-name';
import { evaluateServerPrice } from '@hcltech/shared/server-config/server-price';
import {
  filterPool, CAND_LIMIT_STEP,
  type ServerConfigData, type ConfigRow, type CandidateGroupState,
  type CandidateShareGroup, type RowRegisterState,
} from './types';

const won = (n: number): string => n.toLocaleString('ko-KR');

/** ⚙ 툴팁용 — 어느 컨디션의 기본 구성을 여는지 밝힌다(마이그 173). */
function condSuffix(d: ServerConfigData): string {
  const c = serverConditionLabel(d.serverCondition);
  return c ? ` (${c})` : '';
}

/** 숫자 input 표시용 — 3자리 콤마. */
const fmtPrice = (v: number | null): string => (v === null ? '' : won(v));

/**
 * 구성명의 섀시 모델 자리 — `chassis.model` 원본(`DL380p Gen8`).
 *
 * 세 경로를 순서대로 본다. 앞의 둘은 같은 `chassis` 행에서 온 같은 값이고,
 * 언제 도착하느냐만 다르다:
 *   1) 섀시 행의 스펙 — 섀시를 방금 고른 흐름(fillSpecAndShorthand 완료 후).
 *   2) 후보 응답의 `chassis.model` — 수정 모드 첫 페인트. 이때 행 스펙은
 *      아직 null 이다(rowFromDealItem 이 lazy 조회로 미룬다).
 *   3) 약식 첫 줄 — 카탈로그에서 모델을 못 구하는 경우의 폴백
 *      (임시등록 섀시 / pre_configured_model 출처 / 옛 데이터). 2026-08-14 확정.
 */
export function chassisModelName(d: ServerConfigData): string | null {
  const c = chassisRow(d);
  const fromSpec = c?.spec?.['model'];
  if (typeof fromSpec === 'string' && fromSpec.trim()) return fromSpec.trim();
  if (d.chassisModel && d.chassisModel.trim()) return d.chassisModel.trim();
  return firstDescriptionLine(c?.description) || null;
}

/**
 * 구성 표 디스크립션 칸에 **보여줄** 문자열.
 *
 * 구성품이 **개별 행**으로 나올 때는 카탈로그 원문(description_raw)을 쓴다
 * (2026-08-04, 서안드레). 약식은 서버를 한 줄로 접어 보여줄 때 쓰는 표기라
 * 행마다 펼쳐 놓으면 오히려 정보가 줄어든다 — '1Gb 4Port' 보다
 * 'Broadcom BCM5719 Ethernet 1Gb 4-port BASE-T OCP3 Adapter' 가 맞다.
 * 원문이 없으면(미등록 파트) 저장된 문구로 폴백한다.
 *
 * ★ 이 함수가 **유일한 정본**이어야 한다. 예전엔 이 식이 렌더에 인라인으로
 *   박혀 있었고, 수량 입력 후의 부분 갱신(rerenderDescCell)은 `row.description`
 *   을 그대로 써넣었다. 두 식이 갈라져 있어서 **수량을 건드리는 순간에만**
 *   원문이 약식으로 바뀌는 증상이 났다(2026-08-14). 표시값을 읽는 곳이
 *   둘 이상이면 반드시 이 함수를 거칠 것.
 *
 * ★ descriptionManual 은 여기서 보지 않는다. 수정 모드의 `rowFromDealItem` 이
 *   "저장된 문구 ≠ 원문" 이면 수기 수정으로 간주하는데, 편집기가 약식을
 *   저장해 온 행이 전부 그 조건에 걸린다. 그 값을 우선하면 기존 구성을 열
 *   때마다 약식이 다시 뜬다.
 */
export function descCellValue(r: ConfigRow): string {
  return r.descriptionRaw || r.description;
}

/**
 * 화면 제목 — `{거래처} {섀시 모델} {신품|중고} 서버 구성`.
 *
 * ★ 2026-08-14 서안드레: 딜명이 아니라 **거래처 + 섀시 모델**이다.
 *   예전엔 딜명을 그대로 앞에 붙였는데, 딜명은 `buildDealName` 규칙상
 *   "거래처 + 첫 품목 파트넘버 + 카테고리" 라서 제목에 파트넘버가 딸려 왔다
 *   (`네오코드아이엔에스 P23449-B21 HDD 중고 서버 구성`). 구성명과 딜명은
 *   트리거도 규칙도 다르다 — 딜명은 품목 확정에서만 다시 만들어진다.
 *
 * 조립은 shared `buildServerConfigName` 이 한다. 딜 상세의 `＋ 서버 추가`
 * 목록이 같은 함수를 쓴다 — 각자 조립하면 "목록에서 고른 그 구성" 과
 * "열어 본 그 구성" 의 이름이 갈라진다.
 *
 * 뷰의 `getTitle()`(브라우저 탭)도 이 함수를 쓴다.
 */
export function serverConfigTitle(d: ServerConfigData): string {
  return buildServerConfigName(d.customerName, chassisModelName(d), d.serverCondition);
}

// ─── H1 + 헤더 카드 ──────────────────────────────────────────────────────────
export function renderHeader(d: ServerConfigData): string {
  const condLabel = d.serverCondition === 'NEW' ? '신품'
    : d.serverCondition === 'USED' ? '중고' : '';
  const model = chassisRow(d)?.description || chassisRow(d)?.optionPart || '선택 필요';

  return `
    <header class="svrcfg-header">
      <h1 class="svrcfg-title">${escapeHtml(serverConfigTitle(d))}</h1>

      <div class="svrcfg-headcard">
        <div class="svrcfg-headcell">
          <span class="svrcfg-headlabel">서버 모델</span>
          <span class="svrcfg-headvalue">${escapeHtml(model)}</span>
        </div>
        <div class="svrcfg-headcell">
          <span class="svrcfg-headlabel">서버 컨디션</span>
          <span class="svrcfg-headvalue">${escapeHtml(condLabel || '선택 필요')}</span>
        </div>
        <!-- 서버 대수는 여기 없다 (CONFIG2-018, HANDOFF 소유표).
             대수는 "이 구성을 몇 대 파는가"라 구성이 아니라 **딜**의 값이다.
             같은 구성을 다른 딜에서 다른 수량으로 쓰므로 구성에 묶으면 안 된다.
             딜 상세의 서버 헤더행에서 고친다. -->
        <!-- 판매가와 완료는 오른쪽 끝으로 묶는다 (2026-08-21 기획).
             왼쪽은 "이 서버가 무엇인가"(모델·컨디션), 오른쪽은 "얼마에 파는가 +
             끝낸다" 다. 판매가가 구성품 합계를 받는 칸이 되면서 표의 하단 합계와
             같은 축(오른쪽)에 있어야 눈이 따라간다. -->
        <div class="svrcfg-headright">
        <!-- 환율 — 오늘 환율을 **보여주기만** 한다 (2026-08-24 현민).
             USD 매입단가를 원화로 환산해 마진을 내는 기준이라 화면에 보여야
             하지만, 고칠 수 있게 하면 이 화면에서 임의로 바꾼 환율로 계산한
             마진이 나온다. 환율을 조정해 계산하는 자리는 견적 모달이다. -->
        <div class="svrcfg-headcell is-fx">
          <span class="svrcfg-headlabel">환율 (KRW/USD)</span>
          <span class="svrcfg-headvalue">${fxText(d.fxRate)}</span>
        </div>
        <div class="svrcfg-headcell is-bundle">
          <span class="svrcfg-headlabel">서버 판매 가격</span>
          <input class="svrcfg-bundle" type="text" inputmode="numeric"
                 data-action="bundle-input" placeholder="서버 판매가격"
                 value="${escapeHtml(d.bundlePrice)}" />
          <span class="svrcfg-headhint">구성품 판매가 합계 자동 반영</span>
        </div>
        <div class="svrcfg-headaction">
          <button class="svrcfg-done" data-action="done"
                  ${d.saving || d.baseLoadError ? 'disabled' : ''}
                  ${d.baseLoadError ? 'title="기본 구성을 불러오지 못했습니다. 다시 시도해 주세요."' : ''}>
            ${d.saving ? '저장 중…' : '완료'}
          </button>
        </div>
        </div>
      </div>

      <div class="svrcfg-warn-region">${renderHeaderNotice(d)}</div>
    </header>
  `;
}

/**
 * 에러 문구만 — 금액 입력 중 input 을 다시 그리지 않도록 분리한 리전.
 *
 * ★ 판매가 관련 경고를 **하나도 띄우지 않는다** (2026-08-12 서안드레).
 *
 *   Config 에서 서버 판매 가격과 구성품 판매가는 모두 **선택 입력**이다.
 *   선택 입력끼리의 합계 비교로 화면에 빨간 문구를 띄우면, 아직 채우는
 *   중인 사람에게 잘못한 것처럼 보인다. 저장도 막지 않으므로 경고할 근거가
 *   없다.
 *
 *   합계 비교는 **견적 모달에서만** 한다. 거기서는 서버 판매 가격이 필수
 *   입력이라 "합계가 맞아야 한다"가 성립하고, 실제로 견적 생성을 막는다
 *   (shared/server-price.ts + quote-math.ts quoteBlockReasons).
 *
 *   shared 의 판매가 규칙 자체는 그대로다 — 이 화면이 그것을 화면 문구로
 *   쓰지 않을 뿐이다. 하단 합계(renderTotals)는 계속 보여 준다.
 *   숫자를 보여 주는 것과 그걸로 사람을 막는 것은 다른 일이다.
 */
export function renderHeaderNotice(d: ServerConfigData): string {
  if (d.error) return `<p class="svrcfg-error">${escapeHtml(d.error)}</p>`;
  return bundleMismatchNotice(d);
}

/**
 * 불일치 표시 — **사람이 판매가를 직접 적었을 때만** (2026-08-21 회의 03:36).
 *
 * 위 주석의 2026-08-12 결정(판매가 경고를 띄우지 않는다)과 어긋나지 않는다.
 * 그 결정의 근거는 '선택 입력끼리 비교해 채우는 중인 사람을 나무라는 꼴'
 * 이었는데, 이제 판매가는 기본적으로 합계를 그대로 비춘다. 그래서 값이
 * 다르다는 것은 채우는 중이라는 뜻이 아니라 **사람이 일부러 다르게 적었다**
 * 는 뜻이고, 그건 알려 줘야 하는 사실이다.
 *
 * 그래도 막지는 않는다 — 문구를 명령형(shared 의 serverPriceBlockMessage)
 * 이 아니라 사실 서술로 쓴다. 막는 것은 여전히 견적 모달의 몫이다.
 */
function bundleMismatchNotice(d: ServerConfigData): string {
  if (!d.bundleManual) return '';
  const bundle = parsePrice(d.bundlePrice);
  const sum    = mirroredBundle(d.rows);
  if (bundle === null || sum === null || bundle === sum) return '';
  const diff = bundle - sum;
  const dir  = diff > 0 ? '많습니다' : '적습니다';
  return `<p class="svrcfg-notice">구성품 판매가 합계 \u20A9${won(sum)} 와 다릅니다 — 서버 판매 가격이 \u20A9${won(Math.abs(diff))} ${dir}.</p>`;
}

/** 첫 CHASSIS 행. */
export function chassisRow(d: ServerConfigData): ConfigRow | undefined {
  return d.rows.find((r) => r.specCategory === 'CHASSIS');
}

/**
 * 판매가 평가 — 번들가 vs 구성품 합계.
 *
 * 서버 대수를 넘기지 않는다(=1). 대수는 Config 의 값이 아니고, 이 화면이 쓰는
 * 것은 `partsTotal`/`blockReason`/`difference` 뿐이라 대수와 무관하다.
 * 대수가 곱해지는 `customerTotal` 은 딜·견적에서 계산한다.
 */
export function priceEval(d: ServerConfigData) {
  return evaluateServerPrice(parsePrice(d.bundlePrice), priceChildren(d.rows));
}

/** 판매가 평가에 넣을 구성품 목록 — 카테고리 미지정 행은 아직 구성품이 아니다. */
function priceChildren(rows: readonly ConfigRow[]): { salePrice: number | null; quantity: number }[] {
  return rows
    .filter((r) => r.specCategory !== '')
    .map((r) => ({ salePrice: r.salePrice, quantity: r.quantity }));
}

/**
 * 서버 판매 가격에 비출 구성품 판매가 합계.
 *
 * 기준은 하단 `판매단가 합계`(configTotals)와 **같다** — 판매가가
 * 하나라도 입력돼 있으면 그 합계를 준다. 한 화면에서 같은 숫자를 두고 한쪽은
 * 보여주면서 다른 쪽은 쓰지 않으면 사람이 그 차이를 설명할 방법이 없다.
 *
 * 전부 입력됐을 때만 주도록 좁혔다가 되돌렸다(2026-08-21). 실제 화면은 값이
 * 있는 구성품만 매기는 경우가 많아, 그 조건으로는 반영도 불일치 표시도 영영
 * 뜨지 않았다. 채우는 중에 숫자가 오르내리는 것은 '실시간으로 합산이 모인다'
 * 는 회의 결정(2026-08-21 03:36)이 의도한 동작이다.
 *
 * 하나도 입력되지 않은 BUNDLE_ONLY 상태만 null 이다 — 비출 값이 없다.
 */
export function mirroredBundle(rows: readonly ConfigRow[]): number | null {
  const ev = evaluateServerPrice(null, priceChildren(rows));
  return ev.pricedCount > 0 ? ev.partsTotal : null;
}

/** 환율 문자열 → number. 0 이하·비수치는 null(환산 불가). */
/** 헤더에 보여줄 환율 문구 — 아직 못 받았으면 '-'. */
export function fxText(v: string): string {
  const n = parseFx(v);
  return n === null ? '-' : n.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
}

export function parseFx(v: string): number | null {
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function parsePrice(v: string): number | null {
  const digits = v.replace(/[^\d]/g, '');
  return digits === '' ? null : Number(digits);
}

// ─── 구성 grid ───────────────────────────────────────────────────────────────
export function renderGrid(d: ServerConfigData): string {
  const rows = d.rows.map((r) => renderRow(d, r)).join('');

  return `
    <section class="svrcfg-section">
      <h2 class="svrcfg-h2">구성</h2>
      <table class="svrcfg-table">
        <thead>
          <tr>
            <th class="svrcfg-c-cat">카테고리</th>
            <th class="svrcfg-c-part">품목</th>
            <th class="svrcfg-c-desc">디스크립션</th>
            <th class="svrcfg-c-qty">수량</th>
            <th class="svrcfg-c-src">소스</th>
            <th class="svrcfg-c-cost">매입단가</th>
            <th class="svrcfg-c-ref">레퍼런스</th>
            <th class="svrcfg-c-price">판매단가</th>
            <th class="svrcfg-c-margin">마진금액</th>
            <th class="svrcfg-c-rate">마진율</th>
            <th class="svrcfg-c-act">작업</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
          ${renderBlankRow(d)}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="11">
              <div class="svrcfg-sum-region">${renderTotals(d)}</div>
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  `;
}

/**
 * 하단 합계 4종 — 금액·수량 입력 중 갱신할 리전 (2026-08-21 기획).
 *
 * 매입단가 합계와 판매단가 합계는 **수량까지 곱한 금액**이다(회의 04:34).
 * 마진금액 합계 = 판매 합계 − 매입 합계, 합산 마진율 = 마진 ÷ 판매 합계.
 */
export function renderTotals(d: ServerConfigData): string {
  const t = configTotals(d.rows, parseFx(d.fxRate));
  const cell = (label: string, value: string): string =>
    `<div class="svrcfg-sumcell"><span class="svrcfg-sumlabel">${escapeHtml(label)}</span><span class="svrcfg-sumvalue">${value}</span></div>`;
  const fx = t.noFx > 0
    ? `<p class="svrcfg-sumnote">환율이 없어 외화 매입단가 ${t.noFx}건을 합계에서 제외했습니다.</p>`
    : '';
  return `
    <div class="svrcfg-sumrow">
      ${cell('매입단가 합계', money(t.cost))}
      ${cell('판매단가 합계', money(t.sale))}
      ${cell('마진금액 합계', money(t.margin))}
      ${cell('합산 마진율', pct(t.rate))}
    </div>
    ${fx}
  `;
}

/** 금액 표기 — 값이 없으면 '-'. 음수는 부호를 앞에 둔다(₩-211,000). */
function money(v: number | null): string {
  if (v === null) return '-';
  return v < 0 ? `₩-${won(-v)}` : `₩${won(v)}`;
}

/** 마진율 표기 — 판매 금액이 없으면 계산할 수 없다. */
function pct(v: number | null): string {
  return v === null ? '확인 필요' : `${v.toFixed(1)}%`;
}

export type ConfigTotals = {
  /** 매입단가 × 수량 합계(원화 환산 후). 입력된 행이 없으면 null. */
  cost:      number | null;
  /** 판매단가 × 수량 합계. 입력된 행이 없으면 null. */
  sale:      number | null;
  /** 판매 − 매입. 둘 중 하나라도 없으면 null. */
  margin:    number | null;
  /** 마진 ÷ 판매 × 100. 판매가 0 이거나 없으면 null. */
  rate:      number | null;
  /** 환율이 없어 원화로 환산하지 못한 외화 매입단가 행 수. */
  noFx:      number;
};

/**
 * 매입단가를 원화로 — 견적과 **같은 규칙**이다(quote-math `rowCostKrw`).
 *   원화면 그대로, 외화면 × 환율. 환율이 없으면 null(계산 불가).
 */
export function costKrw(r: ConfigRow, fx: number | null): number | null {
  if (r.costAmount === null) return null;
  if (!r.costCurrency || r.costCurrency === 'KRW') return Math.round(r.costAmount);
  return fx && fx > 0 ? Math.round(r.costAmount * fx) : null;
}

/**
 * 하단 합계 계산. 외화 매입단가는 `fx` 로 환산해서 더한다.
 *
 * 환율이 없는 상태(조회 실패·사람이 지움)에서만 그 행을 빼고, 뺀 건수를 표
 * 아래에 적는다. 조용히 사라지면 매입 합계가 실제보다 작게 보여 마진이 부풀려진다.
 */
export function configTotals(rows: readonly ConfigRow[], fx: number | null): ConfigTotals {
  let cost: number | null = null;
  let sale: number | null = null;
  let noFx = 0;
  for (const r of rows) {
    if (r.specCategory === '') continue;   // 아직 구성품이 아니다
    const qty = Math.max(1, Math.trunc(r.quantity || 1));
    if (r.costAmount !== null) {
      const krw = costKrw(r, fx);
      if (krw === null) noFx += 1;
      else cost = (cost ?? 0) + krw * qty;
    }
    if (r.salePrice !== null) sale = (sale ?? 0) + Math.round(r.salePrice * qty);
  }
  const margin = cost === null || sale === null ? null : sale - cost;
  const rate   = margin === null || !sale ? null : (margin / sale) * 100;
  return { cost, sale, margin, rate, noFx };
}

/** 한 행의 마진금액 — 매입·판매가 다 있어야 낸다. 수량까지 곱한 금액이다. */
export function rowMargin(r: ConfigRow, fx: number | null): number | null {
  if (r.salePrice === null) return null;
  const krw = costKrw(r, fx);
  if (krw === null) return null;
  const qty = Math.max(1, Math.trunc(r.quantity || 1));
  return Math.round((r.salePrice - krw) * qty);
}

/** 한 행의 마진금액 표시 문자열 — 표와 부분 갱신이 같은 함수를 쓴다. */
/**
 * 매입단가 칸의 title — 환산 전 원래 금액과 통화.
 *
 * 칸에는 원화만 보이므로 그 숫자가 어디서 나왔는지 확인할 방법이 하나는
 * 있어야 한다. 원화 원가면 덧붙이지 않는다.
 */
export function nativeCostTitle(r: ConfigRow): string {
  if (r.isBasePart) return '기본 구성 품목의 매입원가는 섀시 값에 포함됩니다.';
  if (r.costAmount === null) return '';
  if (!r.costCurrency || r.costCurrency === 'KRW') return '';
  return `${r.costCurrency} ${won(r.costAmount)} · 환율로 환산한 값입니다`;
}

export function rowMarginText(r: ConfigRow, fx: number | null): string { return money(rowMargin(r, fx)); }

/** 한 행의 마진율 표시 문자열. */
export function rowRateText(r: ConfigRow, fx: number | null): string { return pct(rowMarginRate(r, fx)); }

/** 한 행의 마진율 — 마진 ÷ (판매단가 × 수량). */
export function rowMarginRate(r: ConfigRow, fx: number | null): number | null {
  const m = rowMargin(r, fx);
  if (m === null || r.salePrice === null) return null;
  const qty = Math.max(1, Math.trunc(r.quantity || 1));
  const saleTotal = r.salePrice * qty;
  return saleTotal === 0 ? null : (m / saleTotal) * 100;
}

function renderRow(d: ServerConfigData, r: ConfigRow): string {
  const isChassis = r.specCategory === 'CHASSIS';

  return `
    <tr class="svrcfg-row ${isChassis ? 'is-chassis' : ''} ${r.specCategory === '' ? 'is-nocat' : ''}"
        data-row-key="${escapeHtml(r.key)}">
      <td class="svrcfg-c-cat">
        ${categoryCell(r)}
      </td>
      <td class="svrcfg-c-part">
        <code class="svrcfg-part">${escapeHtml(r.optionPart || '-')}</code>
        ${r.isUnregistered ? '<span class="svrcfg-chip">임시</span>' : ''}
        <!-- 기본 구성에서 자동으로 담긴 품목(마이그 174). 수량·소스는 기본 구성이
             정하고 매입원가는 0(섀시 값에 포함)이라 이 행에서는 잠긴다. -->
        ${r.isBasePart ? '<span class="svrcfg-chip is-base">기본</span>' : ''}
        <!-- CHASSIS 행의 ✎ 는 Chassis 교체다(품목 검색이 아니라 전용 모달).
             ⚙ 는 이 모델의 기본 구성 관리 — 서로 다른 대상이라 버튼을 나눈다.

             ★ 기본 구성 품목(isBasePart, 마이그 174)에는 ✎ 를 붙이지 않는다.
               기본 품목은 이 모델의 확정 세트라 딜에서 품목 자체를 바꾸면 안 된다
               (수량·소스·매입원가도 이미 잠겨 있다). 품목을 바꾸려면 ⚙ 로 기본
               구성을 편집해야 한다. 남기면 '기본인데 품목 검색으로 갈아끼워지는'
               모순이 생긴다(2026-08-24 현민). -->
        ${isChassis
          ? `<button class="svrcfg-pencil" data-action="chassis-replace"
                     title="Chassis 변경" aria-label="Chassis 변경">✎</button>`
          : r.isBasePart
            ? ''
            : `<button class="svrcfg-pencil" data-action="row-search-open"
                     data-row-key="${escapeHtml(r.key)}" title="품목 수정" aria-label="품목 수정">✎</button>`}
        <!-- 복제 ⧉ — **기본 품목 행에만** 붙인다.
             기본 행은 수량 input 이 disabled 라(기본 구성이 정하는 값) 같은 부품을
             하나 더 다는 방법이 "아래에 똑같이 다시 담기" 뿐이었다. 실제로 배터리·팬을
             매번 손으로 다시 넣고 있어서 버튼 하나로 줄인다(2026-08-20 회의 08:01).

             일반 행에는 안 붙인다 — 거기는 수량을 올리면 그만이라 복제가 같은 일을
             두 방식으로 하게 만든다. -->
        ${r.isBasePart
          ? `<button class="svrcfg-dup" data-action="row-duplicate"
                     data-row-key="${escapeHtml(r.key)}"
                     title="같은 품목을 한 줄 더 추가 (기본 아님 — 수량·소스를 직접 정합니다)"
                     aria-label="품목 복제">⧉</button>`
          : ''}
      </td>
      <td class="svrcfg-c-desc">
      <!-- 표시값은 descCellValue() 하나로만 정한다 — 아래 정의의 ★ 참조. -->
        <input class="svrcfg-desc" type="text" data-action="desc-input"
               data-row-key="${escapeHtml(r.key)}"
               value="${escapeHtml(descCellValue(r))}" />
      </td>
      <td class="svrcfg-c-qty">
        <input class="svrcfg-qty" type="number" min="1" step="1" data-action="qty-input"
               data-row-key="${escapeHtml(r.key)}" value="${r.quantity}"
               ${r.isBasePart ? 'disabled title="기본 구성이 정하는 수량입니다. 다르게 넣으려면 같은 품목을 아래에 따로 추가하세요."' : ''} />
      </td>
      <td class="svrcfg-c-src">${renderSourceSelect(d, r)}</td>
      <!-- 매입단가 — **원화 환산값**을 보여준다. 견적 모달과 같은 규칙이다
           (DealDetailView handleQuoteRowChange, field costKrw): 소스를 고르면
           그 원가를 환율로 환산해 넣고, 직접 적으면 그 값을 원화 원가로 잡으면서
           소스를 해제한다 — 한 칸의 주인은 하나여야 한다.
           원래 통화·금액은 title 로 남긴다(USD 650 이 왜 ₩899,750 으로 보이는지
           그 자리에서 확인할 수 있어야 한다).
           기본 품목은 원가가 섀시 값에 포함돼 0 고정이라 잠근다(마이그 174). -->
      <td class="svrcfg-c-cost">
        <input class="svrcfg-cost" type="text" inputmode="numeric" data-action="cost-input"
               data-row-key="${escapeHtml(r.key)}" placeholder="매입단가"
               value="${escapeHtml(fmtPrice(costKrw(r, parseFx(d.fxRate))))}"
               title="${escapeHtml(nativeCostTitle(r))}"
               ${r.isBasePart ? 'disabled' : ''} />
      </td>
      <td class="svrcfg-c-ref">${renderReference(r)}</td>
      <td class="svrcfg-c-price">
        <input class="svrcfg-price" type="text" inputmode="numeric" data-action="price-input"
               data-row-key="${escapeHtml(r.key)}" placeholder="판매단가"
               value="${escapeHtml(fmtPrice(r.salePrice))}" />
      </td>
      <td class="svrcfg-c-margin">${rowMarginText(r, parseFx(d.fxRate))}</td>
      <td class="svrcfg-c-rate">${rowRateText(r, parseFx(d.fxRate))}</td>
      <td class="svrcfg-c-act">
        ${isChassis
          ? `<button class="svrcfg-gear" data-action="base-open"
                     title="기본 구성 관리${condSuffix(d)}"
                     aria-label="기본 구성 관리${condSuffix(d)}">⚙</button>`
          : `<button class="svrcfg-trash" data-action="row-delete"
                     data-row-key="${escapeHtml(r.key)}" title="삭제" aria-label="삭제">🗑</button>`}
      </td>
    </tr>
    ${categoryWarnRow(r.specCategory === '', 11)}
  `;
}

/**
 * 소스 드롭다운 — 재고 + RFQ 를 한 select 에 합친다(핸드오프: 소스는 하나).
 *
 * value 형식: `type:refId` (재고는 refId 없이 `stock:condition|warehouse`).
 * 원가가 없으면 '확인 필요' 로 보이고 cost 는 빈 값으로 저장한다(0 치환 금지).
 */
export function renderSourceSelect(d: ServerConfigData, r: ConfigRow): string {
  // 기본 구성 품목은 매입 소스가 없다 — 섀시에 딸려오는 물건이라 원가 0 고정.
  //   비활성 select 로 둔다(프리뷰 기준). 한때 '기본 구성 포함' 문구로 대체했는데
  //   같은 행의 수량 칸은 비활성 input 이라 잠긴 칸이 둘인데 모양이 달라져
  //   오히려 헷갈린다는 피드백. 왜 잠겼는지는 title 로 알린다.
  if (r.isBasePart) {
    return sourceSelect(d, r.optionPart, '',
      'disabled title="기본 구성 품목은 섀시 값에 포함되어 소스를 고르지 않습니다."');
  }
  return sourceSelect(d, r.optionPart,
    selectedSourceValue(r.sourceType, r.sourceRefId),
    `data-action="source-select" data-row-key="${escapeHtml(r.key)}"`);
}

/**
 * 소스 select 공통부 — 구성 표 행과 후보 행이 같은 옵션 목록을 쓴다.
 *
 * 옵션 목록·라벨·스냅샷의 정본은 `lib/source-options` 다. 딜 상세의 구성품
 * 행도 같은 select 를 쓰는데, 라벨은 화면 문구가 아니라 `source_label` 로
 * **저장되는 값**이라 두 화면이 각자 조립하면 같은 재고를 골라도 저장값이
 * 갈라진다. 여기서는 그 정본에 이 화면의 데이터를 넘겨 주기만 한다.
 */
function sourceSelect(
  d:             ServerConfigData,
  part:          string,
  selectedValue: string,
  attrs:         string,
): string {
  return sourceSelectHtml({
    pool: d.sources, part, selected: selectedValue, attrs, cls: 'svrcfg-src',
  });
}

/**
 * 카테고리 select.
 *
 * 카탈로그에 없는(임시등록) 품목은 카테고리를 유도할 수 없어 빈 값으로 들어온다.
 * 그때는 select 위에 `카테고리 지정 필요` 경고를 띄운다 — placeholder 문구로만
 * 알리면 "선택 안 함"과 "지정해야 함"이 구분되지 않는다. 완료 검증이 이 행을
 * 막으므로, 왜 막히는지 그 자리에서 보여야 한다.
 */
/**
 * 카테고리는 **이미 정해져 있으면 고정 표시**다.
 *
 * 묻는 것은 "카테고리가 정해졌느냐" 하나뿐이다. select 로 두면 CPU 를 RAM 으로
 * 바꾸는 잘못된 조작이 가능해지므로, 정해진 값은 잠근다.
 *
 * ★ 예전 조건은 `catalogSource !== null` 을 함께 봤다(2026-08-20 정정).
 *   그 시절엔 카탈로그에 없는 품목이 곧 "카테고리가 비어 들어오는 품목" 이라
 *   두 조건이 같은 뜻이었다. 그런데 품목 등록 폼이 카테고리를 **필수**로 받게
 *   되면서 전제가 깨졌다 — 등록 폼을 거친 행은 카테고리가 확정돼 있는데도
 *   catalogSource 가 null 이라(카탈로그 행이 아니므로 당연하다) select 가 열려,
 *   필수로 받아 놓고 곧바로 바꿀 수 있는 모순이 됐다.
 *
 *   지금 구성 표에 미등록 행이 들어오는 경로는 둘이고 이 조건이 그 둘을 가른다.
 *     · 품목 등록 폼      → specCategory 확정 → 고정
 *     · pending 검색 선택 → normalizeHit 이 extras.category 로 채우는데 그 값이
 *                           대부분 비어 있다(실측 1035건 중 15건만 존재)
 *                           → specCategory 가 '' → select 를 연다
 *   후자를 열어 두는 것은 서안드레 확인 사항이다(2026-08-20).
 */
export function isCategoryFixed(r: { specCategory: string }): boolean {
  return r.specCategory !== '';
}

function categoryCell(r: ConfigRow): string {
  if (r.specCategory === 'CHASSIS') return '<span class="svrcfg-cat-fixed">CHASSIS</span>';
  if (isCategoryFixed(r)) {
    return `<span class="svrcfg-cat-fixed">${escapeHtml(r.specCategory)}</span>`;
  }
  return categorySelect(
    r.specCategory,
    `data-action="category-select" data-row-key="${escapeHtml(r.key)}"`,
  );
}

/**
 * 구성 표 / 기본 구성 모달 공용 — 같은 어휘·같은 경고 표기를 쓴다.
 *
 * `warnEmpty=false` 는 **품목 등록 폼** 전용이다. 표에서는 카테고리가 비면
 * 고쳐야 할 상태라 주황 경고색이 맞지만, 등록 폼에서는 아직 채우는 중인
 * 첫 칸이라 경고할 일이 아니다(다른 칸과 색이 달라 보이기만 한다).
 */
export function categorySelect(current: string, attrs: string, warnEmpty = true): string {
  const needPick = current === '' && warnEmpty;
  const opts = SPEC_CATEGORIES
    .filter((c) => c !== 'CHASSIS')
    .map((c) => `<option value="${c}" ${current === c ? 'selected' : ''}>${c}</option>`)
    .join('');
  // 경고 문구는 이 셀에 넣지 않는다 — 셀 안에 두면(위든 아래든) 그 칸만
  // 길어져 같은 행의 다른 input 과 수평이 어긋난다. 아래 별도 행으로 뺀다.
  return `
    <select class="svrcfg-catsel ${needPick ? 'is-empty' : ''}" ${attrs}>
      <option value="" ${current === '' ? 'selected' : ''}>카테고리 선택</option>
      ${opts}
    </select>
  `;
}

/**
 * `카테고리 지정 필요` 경고 — 해당 행 **바로 아래의 독립 행**.
 * 셀 높이에 영향을 주지 않아야 같은 행의 컨트롤들이 한 줄로 유지된다.
 */
/**
 * 레퍼런스 — 이 파트의 최근 견적가·최근 판매가 (2026-08-21 기획).
 *
 * ★ 아직 데이터가 없다. 칸만 먼저 만든다.
 *   지금 화면이 들고 있는 것은 매입 쪽 후보(재고·RFQ)뿐이고, 기획이 요구하는
 *   것은 **판매 쪽 이력**(과거 견적 단가·과거 판매 단가)이다. 그건 딜 상세의
 *   레퍼런스 팝오버가 쓰는 데이터라 파트넘버 배열로 한 번에 받는 조회가
 *   따로 필요하다. 매입 후보 숫자를 판매 레퍼런스 자리에 넣으면 라벨과 값이
 *   어긋난 채로 사람이 그 숫자를 보고 값을 매긴다 — 그게 더 나쁘다.
 */
function renderReference(_r: ConfigRow): string {
  return '<span class="svrcfg-ref-empty">없음</span>';
}

function categoryWarnRow(needPick: boolean, colspan: number): string {
  if (!needPick) return '';
  return `
    <tr class="svrcfg-warnrow">
      <td colspan="${colspan}"><span class="svrcfg-catwarn">카테고리 지정 필요</span></td>
    </tr>
  `;
}

/** 저장하지 않는 trailing blank row — 품목 검색만 노출. */
function renderBlankRow(d: ServerConfigData): string {
  const open = d.rowSearch.rowKey === '__blank__';
  return `
    <tr class="svrcfg-row is-blank">
      <td class="svrcfg-c-cat"><span class="svrcfg-addlabel">새 품목 추가</span></td>
      <td colspan="2">
        <input class="svrcfg-search" type="text" data-action="blank-search"
               placeholder="파트넘버 또는 디스크립션 검색"
               value="${escapeHtml(open ? d.rowSearch.query : '')}" />
      </td>
      <td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td>
    </tr>
  `;
}

/**
 * 검색 결과 팝오버 — blank row / 행 재선택 공용.
 *
 * 앵커(검색 input) 아래에 `position: fixed` 로 떠 있다. 표 안에 행으로 넣으면
 * 섹션이 아래로 늘어나고, 입력 중 표를 다시 그리게 되어 포커스도 잃는다.
 */
export function renderRowSearchPanel(d: ServerConfigData): string {
  const s = d.rowSearch;
  if (s.rowKey === null) return '';
  const style = `left:${Math.round(s.x)}px; top:${Math.round(s.y)}px; `
    + `min-width:${Math.max(Math.round(s.width), 420)}px;`;

  // blank row 는 표 안에 검색 input 이 이미 있다. ✎(품목 수정)와 기본 구성
  // 모달의 품목 선택은 앵커가 **버튼**이라 입력할 곳이 없다 → 팝오버가 갖는다.
  const head = s.rowKey === '__blank__' ? '' : `
    <div class="svrcfg-panel-head">
      <input class="svrcfg-search" type="text" data-action="panel-search"
             placeholder="파트넘버 또는 디스크립션 검색"
             value="${escapeHtml(s.query)}" />
    </div>`;

  return `
    <div class="svrcfg-searchpanel" style="${style}">
      ${head}
      <!-- 입력 중에는 이 리전만 다시 그린다 — 위 input 의 caret 을 지킨다. -->
      <div class="svrcfg-panel-body">${renderRowSearchBody(d)}</div>
    </div>
  `;
}

/**
 * 품목 등록 폼 — 카탈로그에 없는 품목을 임시로 등록한다(카탈로그 불변).
 *
 * 프로토타입(admin-ux-preview `configuration2`)과 같은 규격이다:
 *   - 카테고리 목록에서 **CHASSIS 를 뺀다**. 섀시는 전용 모달에서만 고른다.
 *   - 카테고리·파트넘버·디스크립션 **셋 다 필수**.
 *   - 에러는 폼 안에 띄운다(토스트 아님) — 어느 칸을 고쳐야 하는지 보면서
 *     채워야 한다.
 *
 * 예전엔 버튼 하나로 끝나서 검색어가 곧 파트넘버가 됐고, 카테고리·디스크립션을
 * 넣을 자리가 없어 행에 '카테고리 지정 필요' 가 남았다(커밋 37271ca).
 */
function renderRowRegister(g: RowRegisterState): string {
  return `
    <div class="svrcfg-register">
      <h4 class="svrcfg-modal-h4">품목 등록</h4>
      <label class="svrcfg-field">
        <span>카테고리</span>
        ${categorySelect(g.category, 'data-action="row-reg-cat"', false)}
      </label>
      <label class="svrcfg-field">
        <span>파트넘버</span>
        <input class="svrcfg-search" type="text" data-action="row-reg-part"
               placeholder="파트넘버" value="${escapeHtml(g.part)}" />
      </label>
      <label class="svrcfg-field">
        <span>디스크립션</span>
        <input class="svrcfg-search" type="text" data-action="row-reg-desc"
               placeholder="디스크립션" value="${escapeHtml(g.desc)}" />
      </label>
      ${g.error ? `<p class="svrcfg-error">${escapeHtml(g.error)}</p>` : ''}
      <div class="svrcfg-register-foot">
        <button class="svrcfg-btn is-primary" data-action="row-reg-save">등록 및 선택</button>
        <button class="svrcfg-btn" data-action="row-reg-cancel">취소</button>
      </div>
    </div>
  `;
}

/** 검색 결과만 — 입력 중 갱신하는 리전. */
export function renderRowSearchBody(d: ServerConfigData): string {
  const s = d.rowSearch;
  if (s.searching) return '<div class="svrcfg-hint">검색 중…</div>';
  if (s.error)     return `<div class="svrcfg-error">${escapeHtml(s.error)}</div>`;

  if (s.hits.length === 0) {
    // 아직 아무것도 치지 않았으면 '없다'가 아니라 '치세요'다.
    if (s.query.trim() === '') {
      return '<div class="svrcfg-hint">파트넘버 또는 디스크립션을 입력하세요.</div>';
    }
    return `
      <div class="svrcfg-hint">일치하는 품목이 없습니다.</div>
      ${s.register.open ? renderRowRegister(s.register) : `
      <div class="svrcfg-panel-foot">
        <button class="svrcfg-btn svrcfg-btn-sm" data-action="row-register">품목 등록</button>
      </div>`}`;
  }

  return s.hits.map((h) => `
    <button class="svrcfg-hit" data-action="hit-pick"
            data-part="${escapeHtml(h.option_part)}"
            data-source="${escapeHtml(h.source)}">
      <code>${escapeHtml(h.option_part)}</code>
      <span>${escapeHtml(catalogHitDescription(h))}</span>
    </button>
  `).join('');
}

// ─── Chassis 선택/교체 모달 ──────────────────────────────────────────────────
export function renderChassisModal(d: ServerConfigData): string {
  const m = d.chassisModal;
  if (!m.open) return '';

  return `
    <!-- 항상 backdrop 액션을 단다. 최초 선택(취소 불가)인지는 핸들러가 판단한다 —
         조건부로 액션을 빼면 그때만 위임 경로가 달라져 동작이 갈린다. -->
    <div class="svrcfg-modal-backdrop" data-action="chassis-backdrop">
      <div class="svrcfg-modal" role="dialog" aria-modal="true" aria-label="Chassis 선택">
        <div class="svrcfg-modal-head">
          <h3 class="svrcfg-modal-title">${m.replace ? 'Chassis 변경' : 'Chassis 선택'}</h3>
          <p class="svrcfg-modal-desc">
            ${m.replace
              ? '변경 완료를 누를 때만 반영됩니다. 취소하면 기존 구성이 유지됩니다.'
              : '새 서버 구성은 Chassis를 먼저 선택해야 시작할 수 있습니다.'}
          </p>
        </div>

        <fieldset class="svrcfg-fieldset">
          <legend>서버 컨디션 <span class="svrcfg-req">*</span></legend>
          <div class="svrcfg-radios">
            <label><input type="radio" name="svrcfg-cond" value="NEW"
                          data-action="chassis-cond" ${m.pickedCondition === 'NEW' ? 'checked' : ''}/> 신품</label>
            <label><input type="radio" name="svrcfg-cond" value="USED"
                          data-action="chassis-cond" ${m.pickedCondition === 'USED' ? 'checked' : ''}/> 중고</label>
          </div>
        </fieldset>

        <section class="svrcfg-modal-sec">
          <h4 class="svrcfg-modal-h4">Chassis 검색</h4>
          <input class="svrcfg-search" type="text" data-action="chassis-search"
                 placeholder="파트넘버 또는 디스크립션 검색" value="${escapeHtml(m.query)}" />
          <!-- 검색 결과만 갱신하는 리전 — 입력 중 input 을 다시 그리지 않는다. -->
          <div class="svrcfg-hits-region">${renderChassisHits(d)}</div>
          <!-- stage 표시는 별도 리전 — 후보를 클릭해도 결과 목록 DOM 을 유지해야
               뒤따르는 dblclick 이 살아남는다(Sola 위임은 root.contains 검사). -->
          <div class="svrcfg-staged-region">${renderChassisStaged(d)}</div>
        </section>

        <section class="svrcfg-modal-sec">
          <div class="svrcfg-register-region">${renderChassisRegisterRegion(d)}</div>
        </section>

        <div class="svrcfg-modal-foot-wrap">${renderChassisFoot(d)}</div>
      </div>
    </div>
  `;
}

/** 검색 결과 + stage 표시 — 입력 중 갱신할 리전. */
export function renderChassisHits(d: ServerConfigData): string {
  const m = d.chassisModal;
  // 빈/공백 검색은 카탈로그를 나열하지 않는다.
  const hits = m.searching
    ? '<p class="svrcfg-modal-hint">검색 중…</p>'
    : m.query.trim() === ''
      ? '<p class="svrcfg-modal-hint">Chassis 파트넘버 또는 디스크립션을 입력해 검색해 주세요.</p>'
      : m.hits.length === 0
        ? '<p class="svrcfg-modal-hint">검색 결과가 없습니다. 카탈로그에 없으면 아래에서 품목 등록을 사용하세요.</p>'
        : `<div class="svrcfg-modal-hits">${m.hits.map((h) => `
            <button class="svrcfg-hit ${m.pickedPart === h.option_part ? 'is-picked' : ''}"
                    data-action="chassis-pick" data-part="${escapeHtml(h.option_part)}"
                    data-desc="${escapeHtml(catalogHitDescription(h))}">
              <code>${escapeHtml(h.option_part)}</code>
              <span>${escapeHtml(catalogHitDescription(h))}</span>
            </button>
          `).join('')}</div>`;

  return hits;
}

/** stage 된 선택 표시 — 별도 리전. */
export function renderChassisStaged(d: ServerConfigData): string {
  const m = d.chassisModal;
  if (!m.pickedPart) return '';
  return `<p class="svrcfg-modal-staged">
            선택됨 <code>${escapeHtml(m.pickedPart)}</code>
            <span>${escapeHtml(m.pickedDesc)}</span>
          </p>`;
}

/** 품목 등록 영역 — 버튼 또는 펼친 폼. */
export function renderChassisRegisterRegion(d: ServerConfigData): string {
  const m = d.chassisModal;
  return m.registerOpen
    ? renderChassisRegister(m)
    : '<button class="svrcfg-btn" data-action="chassis-register-open">품목 등록</button>';
}

/** 에러 + 확정/취소 버튼 — 선택 상태에 따라 disabled 가 바뀌는 리전. */
export function renderChassisFoot(d: ServerConfigData): string {
  const m = d.chassisModal;
  const canClose = m.replace;
  const canDone  = !!m.pickedPart && !!m.pickedCondition;
  return `
    ${m.error ? `<p class="svrcfg-error">${escapeHtml(m.error)}</p>` : ''}
    <footer class="svrcfg-modal-foot">
      <button class="svrcfg-btn is-primary" data-action="chassis-confirm" ${canDone ? '' : 'disabled'}>
        ${m.replace ? '변경 완료' : '선택 완료'}
      </button>
      ${canClose ? '<button class="svrcfg-btn" data-action="chassis-cancel">취소</button>' : ''}
    </footer>
  `;
}

/**
 * 품목 등록(inline) — 카탈로그에 없는 Chassis 를 임시로 등록한다.
 * canonical 카탈로그를 변경하지 않으며, 컨디션을 먼저 고르지 않으면 등록할 수 없다.
 */
function renderChassisRegister(m: ServerConfigData['chassisModal']): string {
  const canRegister = !!m.registerPart.trim() && !!m.pickedCondition;
  return `
    <div class="svrcfg-register">
      <h4 class="svrcfg-modal-h4">품목 등록</h4>
      <p class="svrcfg-modal-hint">CHASSIS 카테고리로 등록됩니다.</p>
      <label class="svrcfg-field">
        <span>파트넘버</span>
        <input class="svrcfg-search" type="text" data-action="chassis-reg-part"
               placeholder="파트넘버" value="${escapeHtml(m.registerPart)}" />
      </label>
      <label class="svrcfg-field">
        <span>디스크립션</span>
        <input class="svrcfg-search" type="text" data-action="chassis-reg-desc"
               placeholder="디스크립션" value="${escapeHtml(m.registerDesc)}" />
      </label>
      ${!m.pickedCondition
        ? '<p class="svrcfg-modal-hint">서버 컨디션을 먼저 선택해 주세요.</p>'
        : ''}
      <div class="svrcfg-register-foot">
        <button class="svrcfg-btn is-primary" data-action="chassis-reg-confirm"
                ${canRegister ? '' : 'disabled'}>등록 및 선택</button>
        <button class="svrcfg-btn" data-action="chassis-register-close">취소</button>
      </div>
    </div>
  `;
}

// ─── 파트 후보 (Chassis 선택 후) ─────────────────────────────────────────────
/**
 * 카테고리별 후보 아코디언 — **사람이 담은 것만** 그린다.
 *
 * 원천은 `chassis_candidate_part`(장바구니)다. QuickSpec 호환 목록은 추천이라
 * 여기에 나오지 않고, 후보 관리 모달 안에서만 `추천` 배지로 보인다.
 * 담긴 것이 없는 카테고리는 그룹을 만들지 않고 '후보 없음' chip 으로만 노출한다
 * (장바구니 뷰에 안 담긴 카테고리를 18줄 나열하면 화면의 의미가 흐려진다).
 *
 * 후보는 checkbox 가 아니라 `추가` 로 명시적으로 구성에 넣는다. 이미 구성에
 * 있으면 비상호작용 `추가됨` 을 보이고, grid 에서 삭제하면 다시 `추가` 가 된다.
 * 행에는 수량/판매가 input 을 노출하지 않는다(구성 표에서 편집).
 */
export function renderCandidates(d: ServerConfigData): string {
  const chassis = chassisRow(d);
  if (!chassis) {
    return `
      <section class="svrcfg-section">
        <h2 class="svrcfg-h2">호환 파트 후보</h2>
        <p class="svrcfg-hint">Chassis를 먼저 선택해 주세요.</p>
        <p class="svrcfg-hint">Chassis 선택 후 호환 파트 후보를 확인할 수 있습니다.</p>
      </section>
    `;
  }

  const c = d.candidates;
  const body = c.loading
    ? '<p class="svrcfg-hint">후보를 불러오는 중…</p>'
    : c.error
      ? `<p class="svrcfg-error">${escapeHtml(c.error)}</p>`
      // ★ 카테고리 그룹은 담긴 후보가 없어도 **항상** 그린다.
      //   후보 관리 진입점이 각 그룹 헤더의 버튼이므로, 빈 카테고리를 숨기면
      //   담을 방법이 화면에서 사라진다.
      : c.groups.map((g) => renderCandidateGroup(d, g)).join('');

  return `
    <section class="svrcfg-section">
      <h2 class="svrcfg-h2">호환 파트 후보</h2>
      <p class="svrcfg-hint"${shareTitle(c.shareGroup)}>
        ${candidateScopeText(d)}
      </p>
      <div class="svrcfg-cand-groups">${body}</div>
    </section>
  `;
}

/**
 * 후보 섹션 안내 문구 — **줄을 늘리지 않고 기존 한 줄을 갈아끼운다**(기획 목업 유지).
 *
 * 마이그 172 로 후보의 주인이 섀시에서 그룹으로 옮겨갔다. 그러면 원래 문구
 * "{이 SKU} 기준 호환 후보입니다" 가 사실이 아니게 된다 — 이 SKU 기준이
 * 아니라 그룹 기준이고, 여기서 빼면 형제 SKU 에서도 사라진다. 편집 파급을
 * 모른 채 지우는 사고를 막으려면 화면이 사실을 말해야 한다.
 *
 * 그룹이 없거나(172 이후 새로 들어온 섀시) 단독이면 서버가 null 로 접어
 * 보내므로 예전 문구 그대로다.
 */
function candidateScopeText(d: ServerConfigData): string {
  const g = d.candidates.shareGroup;
  if (g && g.skuCount > 1) {
    return `${escapeHtml(g.label)} 그룹 기준 호환 후보입니다. (${g.skuCount}개 SKU 공용)`;
  }
  const chassis = chassisRow(d);
  const name = chassis ? (chassis.description || chassis.optionPart) : '';
  return `${escapeHtml(name)} 기준 호환 후보입니다.`;
}

/** 형제 SKU 목록은 title 로만 단다 — 본문에 6개를 늘어놓으면 문장이 묻힌다. */
function shareTitle(g: CandidateShareGroup | null): string {
  if (!g || g.skuCount <= 1) return '';
  return ` title="${escapeHtml(g.members.join(', '))}"`;
}

function renderCandidateGroup(d: ServerConfigData, g: CandidateGroupState): string {
  const cat = escapeHtml(g.specCategory);
  return `
    <details class="svrcfg-cand-group" data-cat="${cat}" ${g.open ? 'open' : ''}>
      <summary data-action="cand-toggle" data-cat="${cat}">
        <span class="svrcfg-cand-cat">${cat}</span>
        <span class="svrcfg-cand-count">${g.count}개 후보</span>
        <!-- summary 안의 버튼이므로 클릭이 아코디언을 토글하지 않게 핸들러에서 멈춘다. -->
        <button class="svrcfg-btn svrcfg-btn-sm" data-action="cand-manage"
                data-cat="${cat}">후보 관리</button>
      </summary>
      <!-- 본문은 클릭 대상이 아니다. 접기/펼치기는 위 summary 만 한다 —
           목록을 읽다가 아무 데나 눌러서 접히면 후보를 고르는 흐름이 끊긴다.
           (한때 본문 아무 곳이나 눌러도 접히게 했다가 되돌린 자리다) -->
      <div class="svrcfg-cand-body" data-cat="${cat}">${renderCandidateBody(d, g)}</div>
    </details>
  `;
}

/**
 * 한 그룹의 표.
 *
 * 검색·'더 보기'는 두지 않는다. 여기 나오는 것은 사람이 담은 몇 건뿐이고,
 * 담을 때 쓰는 검색은 후보 관리 모달에 있다(같은 화면에 검색이 두 개 있으면
 * 무엇을 검색하는 것인지 모호해진다).
 */
function renderCandidateBody(d: ServerConfigData, g: CandidateGroupState): string {
  const cat = escapeHtml(g.specCategory);

  if (g.items.length === 0) {
    return `<p class="svrcfg-hint svrcfg-cand-empty">
      담긴 후보가 없습니다. 위 <b>후보 관리</b>로 담으세요.
    </p>`;
  }

  const rows = g.items.map((it) => {
    const added = d.rows.some(
      (r) => r.specCategory === g.specCategory && r.optionPart === it.option_part,
    );
    const part = escapeHtml(it.option_part);
    return `
      <tr class="svrcfg-cand-row">
        <td class="svrcfg-cand-add">
          ${added
            ? '<span class="svrcfg-cand-added">추가됨</span>'
            : `<button class="svrcfg-btn svrcfg-btn-sm" data-action="cand-add"
                       data-cat="${cat}" data-part="${part}">추가</button>`}
        </td>
        <td class="svrcfg-cand-part">
          <code>${part}</code>
          ${it.is_recommended ? '<span class="svrcfg-cand-def" title="QuickSpec 호환 목록에 있음">추천</span>' : ''}
          ${it.is_missing ? '<span class="svrcfg-chip" title="카탈로그에서 찾을 수 없음">확인 필요</span>' : ''}
          <span class="svrcfg-cand-desc">${escapeHtml(it.description_raw ?? '')}</span>
        </td>
        <td class="svrcfg-cand-src">
          ${sourceSelect(d, it.option_part, d.candidateSource[it.option_part] ?? '',
            `data-action="cand-source" data-part="${part}"`)}
        </td>
      </tr>
    `;
  }).join('');

  return `
    <table class="svrcfg-cand-table">
      <thead>
        <tr><th class="svrcfg-cand-add">추가</th><th>품목</th><th class="svrcfg-cand-src">소스</th></tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

// ─── 기본 구성 관리 모달 ─────────────────────────────────────────────────────
/**
 * `{모델} 기본 구성 관리` — 이 Chassis 모델의 재사용 구성(staged).
 *
 * 후보와 다른 것이다: 후보는 "고를 거리", 기본 구성은 "자동으로 넣을 확정 세트
 * + 수량"이다. 그래서 여기에는 수량이 있고 소스/가격이 없다.
 *
 * 완료는 두 가지를 한다 — 모델에 저장하고, **현재 구성 표의 부품을 이 세트로
 * 대체**한다(CHASSIS 행은 유지). 그래서 경고 문구를 위에 고정으로 둔다.
 * 빈 목록으로 완료하면 저장된 기본 구성을 지운다(유효한 완료).
 */
/**
 * 컨디션 교체 배너 — 컨디션을 바꿨고, 그 컨디션에 저장된 기본 구성이 있을 때만.
 *
 * confirm() 을 쓰지 않는다: 이 화면의 다른 확인은 전부 인라인이고, 대화상자로
 * 흐름을 막으면 "예" 를 반사적으로 누르게 된다. 대신 무슨 일이 일어나는지를
 * 문구에 그대로 쓴다 — 교체되는 건 **기본 구성 품목뿐**이고 직접 담은 품목은
 * 남는다(2026-08-14 현민).
 */
/**
 * 기본 구성 관련 배너 묶음 — 조회 실패(치명) 위, 컨디션 교체 제안(선택) 아래.
 * 둘은 동시에 뜰 수 있다(컨디션을 바꿔 배너가 떠 있는 채로 모델을 또 바꾸는 등).
 */
export function renderBaseBanners(d: ServerConfigData): string {
  return renderBaseLoadError(d) + renderBaseSwap(d);
}

/**
 * 기본 구성 **조회 실패** 배너.
 *
 * 이 상태에서는 구성 표를 건드리지 않았다 — 기본 구성이 있는지 없는지 모르는데
 * 그걸 근거로 지우면 사용자가 이유도 모른 채 구성을 잃는다. 대신 표에는 이전
 * 섀시의 기본 품목이 남아 있을 수 있으므로 저장을 막고(handleDone) 재시도를 준다.
 */
function renderBaseLoadError(d: ServerConfigData): string {
  const e = d.baseLoadError;
  if (!e) return '';
  return `
    <div class="svrcfg-swap is-error" role="alert">
      <p class="svrcfg-swap-text">
        <strong>기본 구성을 불러오지 못했습니다.</strong>
        ${escapeHtml(e.message)}<br>
        구성 표는 그대로 두었습니다 — 이전 섀시의 기본 품목이 남아 있을 수 있어
        <strong>다시 시도하기 전까지 저장할 수 없습니다.</strong>
      </p>
      <div class="svrcfg-swap-acts">
        <button class="svrcfg-btn svrcfg-btn-sm is-primary" data-action="base-retry">
          다시 시도
        </button>
      </div>
    </div>
  `;
}

function renderBaseSwap(d: ServerConfigData): string {
  const s = d.baseSwap;
  if (!s) return '';
  const cond = serverConditionLabel(s.condition);
  return `
    <div class="svrcfg-swap" role="status">
      <p class="svrcfg-swap-text">
        서버 컨디션을 <strong>${escapeHtml(cond)}</strong>(으)로 바꿨습니다.
        이 모델의 ${escapeHtml(cond)} 기본 구성(${s.items.length}개 품목)을 적용하면
        <strong>지금 담긴 기본 구성 품목이 모두 교체됩니다</strong>.
        직접 담은 품목과 Chassis 는 그대로 유지됩니다.
      </p>
      <div class="svrcfg-swap-acts">
        <button class="svrcfg-btn svrcfg-btn-sm is-primary" data-action="base-swap-apply">
          ${escapeHtml(cond)} 기본 구성으로 교체
        </button>
        <button class="svrcfg-btn svrcfg-btn-sm" data-action="base-swap-close">
          현재 구성 유지
        </button>
      </div>
    </div>
  `;
}

export function renderBaseModal(d: ServerConfigData): string {
  const m = d.baseModal;
  if (!m.open) return '';
  const chassis = chassisRow(d);
  const model = chassis?.description || chassis?.optionPart || '';
  // 기본 구성은 모델×컨디션 단위다(마이그 173) — 제목에 어느 쪽을 고치는지 박는다.
  //   컨디션이 제목에 없으면 신품 구성을 열어 놓고 중고를 고치고 있다고 착각한다.
  const cond = serverConditionLabel(m.condition);
  const empty = !m.loading && !m.exists && m.items.length === 0;

  return `
    <div class="svrcfg-modal-backdrop" data-action="base-backdrop">
      <div class="svrcfg-modal svrcfg-modal-base" role="dialog" aria-modal="true"
           aria-label="기본 구성 관리">
        <div class="svrcfg-modal-head">
          <h3 class="svrcfg-modal-title">
            ${escapeHtml(model)} 기본 구성 관리
            <span class="svrcfg-cond-chip">${escapeHtml(cond)}</span>
          </h3>
          <p class="svrcfg-modal-desc">
            현재 Chassis 모델의 <strong>${escapeHtml(cond)}</strong> 구성만 편집합니다.
            다른 컨디션의 기본 구성은 서버 컨디션을 바꾼 뒤 다시 열어 편집합니다.
          </p>
        </div>

        <section class="svrcfg-modal-sec">
          ${empty ? `
          <p class="svrcfg-hint svrcfg-base-empty">
            이 모델의 ${escapeHtml(cond)} 기본 구성은 아직 없습니다.
            아래에서 새로 만들어 저장하세요.
          </p>` : ''}
          <p class="svrcfg-warn svrcfg-base-warn">
            기본 구성을 지정하면 현재 추가된 구성 품목이 제거되고 기본 구성으로 대체됩니다
          </p>
          <div class="svrcfg-base-tools">
            <button class="svrcfg-btn svrcfg-btn-sm" data-action="base-add">추가</button>
          </div>
          <div class="svrcfg-base-region">${renderBaseRows(d)}</div>
        </section>

        <footer class="svrcfg-modal-foot">
          <button class="svrcfg-btn is-primary" data-action="base-done"
                  ${m.saving || m.loading ? 'disabled' : ''}>
            ${m.saving ? '저장 중…' : '완료'}
          </button>
          <button class="svrcfg-btn" data-action="base-cancel">취소</button>
          ${m.error ? `<span class="svrcfg-error">${escapeHtml(m.error)}</span>` : ''}
        </footer>
      </div>
    </div>
  `;
}

/** 기본 구성 행 목록 — 추가/삭제/품목선택으로 갱신하는 리전. */
export function renderBaseRows(d: ServerConfigData): string {
  const m = d.baseModal;
  if (m.loading) return '<p class="svrcfg-hint">기본 구성을 불러오는 중…</p>';

  const rows = m.items.map((r) => {
    const key = escapeHtml(r.key);
    return `
      <tr class="svrcfg-base-row ${r.specCategory === '' ? 'is-nocat' : ''}" data-row-key="${key}">
        <td class="svrcfg-base-cat">
          ${isCategoryFixed(r)
            ? `<span class="svrcfg-cat-fixed">${escapeHtml(r.specCategory)}</span>`
            : categorySelect(r.specCategory, `data-action="base-cat" data-row-key="${key}"`)}
        </td>
        <td class="svrcfg-base-part">
          <button class="svrcfg-base-pick" data-action="base-search-open" data-row-key="${key}">
            ${r.optionPart
              ? `<code>${escapeHtml(r.optionPart)}</code>`
              : '<span class="svrcfg-hint">품목 선택</span>'}
            ${r.isUnregistered ? '<span class="svrcfg-chip">임시</span>' : ''}
          </button>
        </td>
        <td class="svrcfg-base-desc">
          <input class="svrcfg-desc" type="text" data-action="base-desc"
                 data-row-key="${key}" value="${escapeHtml(r.description)}" />
        </td>
        <td class="svrcfg-base-qty">
          <input class="svrcfg-qty" type="number" min="1" step="1" data-action="base-qty"
                 data-row-key="${key}" value="${r.quantity}" />
        </td>
        <td class="svrcfg-base-act">
          <button class="svrcfg-trash" data-action="base-del" data-row-key="${key}"
                  title="삭제" aria-label="삭제">🗑</button>
        </td>
      </tr>
      ${categoryWarnRow(r.specCategory === '', 5)}
    `;
  }).join('');

  return `
    <table class="svrcfg-base-table">
      <thead>
        <tr>
          <th class="svrcfg-base-cat">카테고리</th>
          <th class="svrcfg-base-part">품목</th>
          <th class="svrcfg-base-desc">디스크립션</th>
          <th class="svrcfg-base-qty">수량</th>
          <th class="svrcfg-base-act">작업</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="5" class="svrcfg-base-none">
          ${m.exists
            ? '품목을 모두 지웠습니다. 완료하면 이 모델의 기본 구성이 삭제됩니다.'
            : '추가를 선택해 기본 구성 품목을 입력해 주세요.'}
        </td></tr>`}
      </tbody>
    </table>
  `;
}

// ─── 후보 관리 모달 ──────────────────────────────────────────────────────────
/**
 * `[카테고리] 후보 관리` — 카테고리 하나의 장바구니를 편집한다(staged).
 *
 * pool 은 그 카테고리의 카탈로그 **전량**이다. 그래서 QuickSpec 추천에 없는
 * 파트도 검색해서 담을 수 있다 — 후보는 추천의 부분집합이 아니다.
 *
 * 규칙(핸드오프 §4):
 *   - 검색은 NFC 정규화 + 대소문자 무시 substring. 빈 검색은 전체.
 *   - 체크 상태는 filter 밖에서도 유지한다(파트넘버 Set).
 *   - '전체 선택'은 **보이는 결과만** 대상. 보이는 것 기준으로 3상태 계산.
 *   - 저장은 체크 집합으로 교체(해제=삭제). 취소는 아무것도 남기지 않는다.
 *
 * ★ 체크는 **DB 에 담긴 것만** 이다. 추천을 미리 체크해 두지 않는다 —
 *   사람이 담지 않은 것은 후보가 아니라는 원칙과 어긋나고, 열자마자 저장을
 *   누르면 CPU 321건이 통째로 담긴다.
 *
 * ★ 레이아웃: .svrcfg-modal 은 padding:0 이고 내부 head/sec/foot 블록이 각자
 *   패딩을 갖는 구조다. 이 래퍼 없이 내용을 바로 넣으면 모서리에 붙는다.
 */
export function renderCandidateModal(d: ServerConfigData): string {
  const m = d.candidateModal;
  if (!m.open || m.specCategory === '') return '';
  const cat = escapeHtml(m.specCategory);

  return `
    <!-- backdrop 클릭으로 닫는다. 핸들러가 e.target === backdrop 인지 확인하므로
         안쪽 클릭은 닫히지 않는다(closest() 위임이라 확인이 없으면 다 닫힌다). -->
    <div class="svrcfg-modal-backdrop" data-action="cand-backdrop">
      <div class="svrcfg-modal svrcfg-modal-cand" role="dialog" aria-modal="true"
           aria-label="${cat} 후보 관리">
        <div class="svrcfg-modal-head">
          <h3 class="svrcfg-modal-title">${cat} 후보 관리</h3>
          <p class="svrcfg-modal-desc">
            이 카테고리의 카탈로그 품목 중 후보로 담을 항목을 선택합니다.
            QuickSpec <b>추천</b>이 위에 모이지만, 추천에 없는 품목도 담을 수 있습니다.
          </p>
        </div>

        <section class="svrcfg-modal-sec">
          <h4 class="svrcfg-modal-h4">후보 검색</h4>
          <!-- 검색 input 은 목록 리전 밖 — 입력 중 다시 그리지 않아 caret 이 유지된다. -->
          <input class="svrcfg-search" type="text" data-action="cand-pool-search"
                 placeholder="파트넘버 또는 디스크립션 검색"
                 value="${escapeHtml(m.query)}" />
          <div class="svrcfg-cand-pool-region">${renderCandidatePool(d)}</div>
        </section>

        <footer class="svrcfg-modal-foot">
          <button class="svrcfg-btn is-primary" data-action="cand-save"
                  ${m.saving || m.loading ? 'disabled' : ''}>
            ${m.saving ? '저장 중…' : '저장'}
          </button>
          <button class="svrcfg-btn" data-action="cand-cancel">취소</button>
          <span class="svrcfg-cand-selcount">${m.checked.size}개 선택</span>
        </footer>
      </div>
    </div>
  `;
}

/** 모달의 목록 + 전체선택 — 검색/체크로 갱신하는 리전. */
export function renderCandidatePool(d: ServerConfigData): string {
  const m = d.candidateModal;
  if (m.loading) return '<p class="svrcfg-hint">카탈로그를 불러오는 중…</p>';
  if (m.error)   return `<p class="svrcfg-error">${escapeHtml(m.error)}</p>`;

  const matched = filterPool(m);
  const shown   = matched.slice(0, m.limit);
  const rest    = matched.length - shown.length;

  // 3상태는 **보이는 결과** 기준이다. 검색으로 좁힌 뒤 '전체 선택'을 누르면
  // 그 결과만 토글되어야 하고, 숨은 행의 체크는 건드리지 않는다.
  const visibleChecked = matched.filter((it) => m.checked.has(it.option_part)).length;
  const allChecked     = matched.length > 0 && visibleChecked === matched.length;
  const someChecked    = visibleChecked > 0 && !allChecked;

  const rows = shown.map((it) => {
    const on   = m.checked.has(it.option_part);
    const part = escapeHtml(it.option_part);
    return `
      <tr class="svrcfg-pool-row ${on ? 'is-on' : ''}">
        <td class="svrcfg-pool-chk">
          <input type="checkbox" data-action="cand-check" data-part="${part}"
                 ${on ? 'checked' : ''} aria-label="${part} 후보로 담기" />
        </td>
        <td class="svrcfg-pool-part"><code>${part}</code></td>
        <td class="svrcfg-pool-desc">
          ${it.recommended ? '<span class="svrcfg-cand-def">추천</span>' : ''}
          ${escapeHtml(it.description_raw ?? '')}
        </td>
      </tr>
    `;
  }).join('');

  return `
    ${m.truncated
      ? '<p class="svrcfg-warn">품목이 너무 많아 일부만 불러왔습니다. 검색으로 좁혀 주세요.</p>'
      : ''}
    <div class="svrcfg-pool-wrap">
      <table class="svrcfg-pool-table">
        <thead>
          <tr>
            <th class="svrcfg-pool-chk">
              <input type="checkbox" data-action="cand-check-all"
                     ${allChecked ? 'checked' : ''}
                     ${matched.length === 0 ? 'disabled' : ''}
                     ${someChecked ? 'data-indeterminate="1"' : ''}
                     aria-label="보이는 결과 전체 선택" />
            </th>
            <th class="svrcfg-pool-part">파트넘버</th>
            <th class="svrcfg-pool-desc">디스크립션</th>
          </tr>
        </thead>
        <tbody>${rows || '<tr><td colspan="3" class="svrcfg-hint">검색 결과가 없습니다.</td></tr>'}</tbody>
      </table>
    </div>
    <div class="svrcfg-cand-foot">
      <span class="svrcfg-cand-shown">
        ${shown.length}/${matched.length}개 표시 · 선택 ${visibleChecked}
      </span>
      ${rest > 0
        ? `<button class="svrcfg-btn svrcfg-btn-sm" data-action="cand-pool-more">
             더 보기 (${Math.min(CAND_LIMIT_STEP, rest)}개)
           </button>`
        : ''}
    </div>
  `;
}
